import { useContext, useEffect, useRef } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { StationRouteContext, useCrudEditContext } from "@ryde/redux";
import {
  editFormDataAF,
  EditFormMode,
  getRealRouteStationIds,
  IStationRouteDto,
  StationRouteApi,
  stationRouteMatchingMlPathIdsAtom,
  stationRouteMatchingMlPathUpdateIdAtom,
  useSystemParams,
} from "@ryde/shared";

/** Keeps matching-ML-path footer atoms in sync with the form station set (ride-opened drawer only). */
export const useStationRouteMatchingMlPathsSync = (): void => {
  const { rideOpenerId } = useContext(StationRouteContext);
  const { entity, mode } = useCrudEditContext<IStationRouteDto>();
  const stationRouteDto: IStationRouteDto | undefined = useRecoilValue(
    editFormDataAF([entity, mode || EditFormMode.CrudRoute]),
  );
  const systemParams = useSystemParams();
  const setMatchingMlPathIds = useSetRecoilState(
    stationRouteMatchingMlPathIdsAtom,
  );
  const setMatchingMlPathUpdateId = useSetRecoilState(
    stationRouteMatchingMlPathUpdateIdAtom,
  );
  const preferredId = useRecoilValue(stationRouteMatchingMlPathUpdateIdAtom);
  const preferredIdRef = useRef<number | null>(preferredId);
  preferredIdRef.current = preferredId ?? stationRouteDto?.id ?? null;

  const stationIdsKey = getRealRouteStationIds(
    stationRouteDto?.computedData?.stationInfos,
  ).join(",");

  useEffect(() => {
    if (!rideOpenerId) {
      return;
    }

    const stationIds = stationIdsKey
      ? stationIdsKey.split(",").map(Number).filter(Boolean)
      : [];

    if (stationIds.length < 2) {
      setMatchingMlPathIds([]);
      setMatchingMlPathUpdateId(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      const matchingResult = await StationRouteApi.findMatchingMlPaths({
        stationIds,
        avoidTolls: systemParams.avoidTolls,
        preferredId: preferredIdRef.current,
      });

      if (cancelled || !matchingResult?.success) {
        return;
      }

      setMatchingMlPathIds(matchingResult.data?.ids ?? []);
      setMatchingMlPathUpdateId(matchingResult.data?.updateId ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    rideOpenerId,
    setMatchingMlPathIds,
    setMatchingMlPathUpdateId,
    stationIdsKey,
    systemParams.avoidTolls,
  ]);
};
